import { Abstract, Coordinates } from '@/types/commons/commons.types';

import {
  CommonConfiguration,
  CommonSensorStatus,
  CommonSubSensorStatus,
  EffectiveSensorStatusEnum,
  HasEffectiveStatus,
  HasHealthStatus,
  HasSubSensorStatuses,
  HasUniqueKey,
  HostConfiguration,
  PortConfiguration,
  PositionSubSensor,
  SensorTypeEnum,
  SpecificStateEnum,
  SubSensorTypeEnum,
} from './common.types';

export interface AutomatonPortal {
  id: number;
  appCode: string;
  code: string;
  name: string;
  position: Coordinates;
  portalId: number;
  opened: boolean;
}

export enum AutomatonSegmentTypeEnum {
  LOWER = 'LOWER',
  UPPER = 'UPPER',
}

export interface AutomatonSegment extends PositionSubSensor, Abstract {
  nodeId: number;
  type: SubSensorTypeEnum.AUTOMATON_SEGMENT;
  consigned: boolean;
  segmentType: AutomatonSegmentTypeEnum;
  segmentPosition: Coordinates[];
  perimeterCameraUniqueKeys: string[];
  ladCameraUniqueKey: string | null;
  zone: string;
  simplifiedPosition: Coordinates;
}

export interface AutomatonCabinet extends PositionSubSensor {
  nodeId: number;
  type: SubSensorTypeEnum.AUTOMATON_CABINET;
  segments: AutomatonSegment[];
  portals?: AutomatonPortal[];
}

export interface AutomatonSensorConfiguration extends CommonConfiguration, HostConfiguration, PortConfiguration {
  type: SensorTypeEnum.AUTOMATON;
  username: string;
  password: string;
  cabinets: AutomatonCabinet[];
}

export enum AutomatonNodeStateEnum {
  MISSING_VALUE = 'MISSING_VALUE',
  TECHNICAL_DEFECT_ALARM_CAUSE = 'TECHNICAL_DEFECT_ALARM_CAUSE',
  EFFRACTION = 'EFFRACTION',
  BT_DETECTION = 'BT_DETECTION',
  OK = 'OK',
}

export enum AutomatonCabinetStateEnum {
  MISSING_VALUE = 'MISSING_VALUE',
  SLEEP = 'SLEEP',
  EFFRACTION = 'EFFRACTION',
  TECHNICAL_DEFECT = 'TECHNICAL_DEFECT',
  OK = 'OK',
}

export type SegmentSpecificStateEnum =
  | SpecificStateEnum.BT_DETECTION
  | SpecificStateEnum.HT_DETECTION
  | SpecificStateEnum.EFFRACTION
  | SpecificStateEnum.SLEEP;

export interface AutomatonSegmentStatus extends HasUniqueKey, HasHealthStatus, HasEffectiveStatus {
  parentUniqueKey: string;
  segmentType: AutomatonSegmentTypeEnum;
  states: Partial<Record<AutomatonNodeStateEnum, string[]>>;
  specificState: SegmentSpecificStateEnum | null;
}

type SegmentsStatus =
  | {
      upperSegment: AutomatonSegmentStatus;
      lowerSegment: AutomatonSegmentStatus;
    }
  | {
      upperSegment: null;
      lowerSegment: AutomatonSegmentStatus;
    }
  | { upperSegment: AutomatonSegmentStatus; lowerSegment: null };

export type AutomatonSectionStatus = CommonSubSensorStatus & {
  type: SubSensorTypeEnum.AUTOMATON_SEGMENT;
  nodeId: number;
  specificState: SegmentSpecificStateEnum | null;
} & SegmentsStatus;

export interface AutomatonCabinetStatus extends CommonSubSensorStatus, HasSubSensorStatuses<AutomatonSectionStatus> {
  type: SubSensorTypeEnum.AUTOMATON_CABINET;
  nodeId: number;
  cabinetStates: Partial<Record<AutomatonCabinetStateEnum, string[]>>;
  specificState: SpecificStateEnum.SLEEP | null;
}

export interface AutomatonSensorStatus extends CommonSensorStatus, HasSubSensorStatuses<AutomatonCabinetStatus> {
  type: SensorTypeEnum.AUTOMATON;
}
export type AutomatonSectionStatusEnumWithMaintenance = EffectiveSensorStatusEnum | 'maintenance';

export enum TechnicalDefectEnum {
  CIRCUIT_BREAKER = 'Electrificateur.Sectionneur_Arret_Electrificateur',
  EMERGENCY_STOP = 'Coffret.Arret_Urgence',
}

export interface AutomatonPortalWithStatus extends AutomatonPortal {
  status: AutomatonSectionStatusEnumWithMaintenance;
}

export type AutomatonSection =
  | {
      lowerSegment: AutomatonSegment;
      upperSegment: AutomatonSegment;
    }
  | {
      lowerSegment?: never;
      upperSegment: AutomatonSegment;
    }
  | {
      lowerSegment: AutomatonSegment;
      upperSegment?: never;
    };

export interface AutomatonSegmentKey {
  automaton: string;
  cabinet: string;
  segment: string;
}

export interface AutomatonSectionData {
  automaton: AutomatonSensorConfiguration;
  cabinet: AutomatonCabinet;
  section: AutomatonSection;
  perimeterCameraUniqueKeys: string[];
}

export interface AutomatonSectionUniqueKeys {
  automatonUniqueKey: string;
  cabinetUniqueKey: string;
  sectionName: string;
}
